import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@repo/ui/src/components/ui/button';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import {
  DEFAULT_INVOICE_MESSAGE_TEMPLATE,
  resolveTemplate,
  type InvoiceMessageData,
} from '../../../lib/invoiceMessage';
import { insertAtCursor } from '../../../lib/textInsertion';

/** Click-to-insert chip row — token + Vietnamese label, in spec order. */
const TEMPLATE_TOKENS: { token: string; labelKey: string }[] = [
  { token: '{phu_huynh}', labelKey: 'preferences.templateTokenPayer' },
  { token: '{ky}', labelKey: 'preferences.templateTokenPeriod' },
  { token: '{hoc_vien}', labelKey: 'preferences.templateTokenStudents' },
  { token: '{tong_tien}', labelKey: 'preferences.templateTokenTotal' },
  { token: '{link}', labelKey: 'preferences.templateTokenLink' },
  { token: '{han_thanh_toan}', labelKey: 'preferences.templateTokenDueDate' },
];

/** Fixed mock data for the live preview — chosen to look like the guide's
 *  sample data (spec §3), not tied to any real invoice. */
const TEMPLATE_PREVIEW_DATA: InvoiceMessageData = {
  payerName: 'chị Huyền',
  periodLabel: 'tháng 8/2026',
  students: ['Bảo (Piano)', 'Ân (Vẽ)'],
  invoiceTitleLine: 'Hoá đơn HD-2026-08-001',
  totalFormatted: '1.500.000đ',
  link: 'https://lophoc.app/i/mock-preview',
  dueDateFormatted: '25/08/2026',
};

const TEMPLATE_MAX_LENGTH = 2000;

/**
 * Chips + textarea + live preview + "restore default" for the manual-send
 * message template (THI-87 §3). Pure presentation: the text itself and its
 * dirty flag live in `BillingRulesSection`, which is what saves it — this
 * component only ever calls `onChange` with the next text, including for a
 * chip insert and for the restore-default button.
 */
export function InvoiceTemplateEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation('settings');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // A ref, not state: the caret-restore effect below only needs to mutate the
  // DOM (focus + setSelectionRange), never to trigger a re-render — writing
  // it back via setState would just cascade another render for no visible
  // change (and trips the "no setState in an effect body" lint rule).
  const pendingCaretRef = useRef<number | null>(null);

  // Click-to-insert (spec: chips, no drag-and-drop). Reads the caret/selection
  // off the textarea ref, splices the token in via the pure `insertAtCursor`,
  // then asks the caret-restore effect below to refocus + reposition once
  // React has committed the new value to the DOM (setting selectionRange
  // synchronously here would race the not-yet-updated textarea value).
  function handleInsertToken(token: string) {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const { text, cursor } = insertAtCursor(value, { start, end }, token);
    onChange(text);
    pendingCaretRef.current = cursor;
  }

  function handleRestoreDefault() {
    // No-op guard: text already the default (e.g. clicked twice, or the org
    // never had a custom template) must not flip the section's dirty flag and
    // enable Save into an otherwise-empty-bodied PUT.
    if (value === DEFAULT_INVOICE_MESSAGE_TEMPLATE) return;
    onChange(DEFAULT_INVOICE_MESSAGE_TEMPLATE);
  }

  // Runs after the DOM has the new value committed (useLayoutEffect, not
  // useEffect, so the caret jump never has a visible flash of the old
  // position) — see handleInsertToken. Keyed on `value` so it only fires on
  // the render that follows a chip insert, not on every render.
  useLayoutEffect(() => {
    if (pendingCaretRef.current === null) return;
    const el = textareaRef.current;
    el?.focus();
    el?.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
    pendingCaretRef.current = null;
  }, [value]);

  // Mirrors resolveTemplateSaveValue's empty-floor rule for the preview: an
  // emptied (or whitespace-only) editor is what will actually be SAVED as the
  // default, so the preview must show the default's resolution too — not a
  // blank box that doesn't match what the teacher gets after saving.
  const templateForPreview = value.trim() === '' ? DEFAULT_INVOICE_MESSAGE_TEMPLATE : value;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {TEMPLATE_TOKENS.map(({ token, labelKey }) => (
          <button
            key={token}
            type="button"
            onClick={() => handleInsertToken(token)}
            className="inline-flex items-center rounded-full border border-surface-200 bg-surface-50 px-3 py-1 text-xs font-medium text-surface-600 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            maxLength={TEMPLATE_MAX_LENGTH}
            rows={10}
            className="font-mono text-sm"
            aria-label={t('preferences.templateTitle')}
          />
          <div className="mt-1 flex items-center justify-between">
            <p className="text-xs text-surface-500">
              {t('preferences.templateCharCount', { count: value.length })}
            </p>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium text-surface-500">{t('preferences.templatePreviewTitle')}</p>
          <div className="whitespace-pre-wrap rounded-md border border-surface-200 bg-surface-50 p-3 text-sm text-surface-700">
            {resolveTemplate(templateForPreview, TEMPLATE_PREVIEW_DATA)}
          </div>
        </div>
      </div>

      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs font-medium"
          onClick={handleRestoreDefault}
        >
          {t('preferences.templateRestoreDefault')}
        </Button>
      </div>

      <p className="text-xs text-surface-500">{t('preferences.templateHelper')}</p>
    </div>
  );
}
